import { useEffect, useMemo } from 'react';
import { useScheduledMeetingsQuery } from '../../../contexts';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import {
  filterDraftMeeting,
  filterPersonalMeetingsFromModal,
  filterRestPersonalMeetings,
} from '../helpers/common';
import { sortPersonalMeetings } from '../helpers/sorters';

const useMeetingsQuery = () => {
  const {
    meetings: rawMeetings,
    isLoading,
    isFetching,
    isInitialized,
    error,
    getMeetings,
    refreshMeetings,
  } = useScheduledMeetingsQuery();

  const { handleComponentError } = useErrorHandler('PersonalMeetingsModal');

  // Initial data fetch
  useEffect(() => {
    const shouldFetch = !(isInitialized || isLoading);
    if (shouldFetch) {
      void getMeetings().catch((err) => {
        handleComponentError(err as Error, 'initial_fetch');
      });
    }
  }, [getMeetings, handleComponentError, isInitialized, isLoading]);

  const { meetings, personalMeetings } = useMemo(() => {
    const draftMeetings = rawMeetings.filter(filterDraftMeeting);

    const personalMeetingsFromModal = rawMeetings.filter(filterPersonalMeetingsFromModal);
    const restPersonalMeetings = rawMeetings.filter(filterRestPersonalMeetings);

    const sortedPersonalMeetings = [
      ...sortPersonalMeetings(personalMeetingsFromModal),
      ...sortPersonalMeetings(restPersonalMeetings),
    ];

    return {
      meetings: [...sortedPersonalMeetings, ...draftMeetings],
      personalMeetings: sortedPersonalMeetings,
    };
  }, [rawMeetings]);

  return {
    meetings,
    personalMeetings,
    isLoading,
    isFetching,
    isInitialized,
    queryError: error,
    refreshMeetings,
  };
};

export default useMeetingsQuery;
